import React, { useState, useEffect } from 'react';
import { useAuth } from '../auth/AuthContext';
import { appointmentApi } from '../utils/appointmentApi';
import { Toast, useToast } from '../utils';

export default function AppointmentScheduler({ clientId, onScheduled }) {
  const { user } = useAuth();
  const { showToast, toastMessage, hideToast } = useToast();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [appointmentType, setAppointmentType] = useState('routine');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const APPOINTMENT_TYPES = [
    { id: 'routine', label: 'Routine Check-up' },
    { id: 'initial', label: 'Initial Consultation' },
    { id: 'prenatal', label: 'Prenatal Visit' },
    { id: 'postpartum', label: 'Postpartum Visit' },
    { id: 'emergency', label: 'Emergency Visit' },
  ];

  useEffect(() => {
    fetchAvailableSlots();
  }, [selectedDate]);

  const fetchAvailableSlots = async () => {
    try {
      setLoading(true);
      setError(null);
      const formattedDate = selectedDate.toISOString().split('T')[0];
      const slots = await appointmentApi.getAvailableTimeSlots(user.sub, formattedDate);
      setAvailableSlots(slots);
    } catch (err) {
      setError('Failed to fetch available time slots');
      showToast('Error loading available times');
    } finally {
      setLoading(false);
    }
  };

  const formatTimeSlot = (slot) => {
    const formatTime = (dateTimeString) => {
      return new Date(dateTimeString).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    };
    return `${formatTime(slot.startTime)} - ${formatTime(slot.endTime)}`;
  };

  const handleScheduleAppointment = async () => {
    if (!selectedSlot) {
      showToast('Please select a time slot');
      return;
    }

    try {
      setLoading(true);
      const appointmentData = {
        midwifeId: user.sub,
        clientId,
        startTime: selectedSlot.startTime,
        endTime: selectedSlot.endTime,
        type: appointmentType,
        status: 'scheduled'
      };

      await appointmentApi.createAppointment(appointmentData);
      showToast('Appointment scheduled successfully');
      if (onScheduled) onScheduled();
    } catch (err) {
      setError('Failed to schedule appointment');
      showToast('Error scheduling appointment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow p-6">
      <h2 className="text-xl font-semibold text-emerald-700 mb-6">Schedule Appointment</h2>
      
      {/* Appointment Type Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Appointment Type
        </label>
        <select
          value={appointmentType}
          onChange={(e) => setAppointmentType(e.target.value)}
          className="w-full border-gray-300 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500"
        >
          {APPOINTMENT_TYPES.map(type => (
            <option key={type.id} value={type.id}>{type.label}</option>
          ))}
        </select>
      </div>

      {/* Date Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Date
        </label>
        <input
          type="date"
          value={selectedDate.toISOString().split('T')[0]}
          onChange={(e) => setSelectedDate(new Date(e.target.value))}
          min={new Date().toISOString().split('T')[0]}
          className="w-full border-gray-300 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500"
        />
      </div>

      {/* Time Slots */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Available Time Slots
        </label>
        {loading ? (
          <div className="text-center py-4">Loading available times...</div>
        ) : error ? (
          <div className="text-red-600 py-2">{error}</div>
        ) : availableSlots.length === 0 ? (
          <div className="text-gray-500 py-2">No available time slots for this date</div>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {availableSlots.map((slot, index) => (
              <button
                key={index}
                onClick={() => setSelectedSlot(slot)}
                className={`p-2 rounded-md text-sm font-medium ${
                  selectedSlot === slot
                    ? 'bg-emerald-600 text-white'
                    : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                }`}
              >
                {formatTimeSlot(slot)}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Schedule Button */}
      <button
        onClick={handleScheduleAppointment}
        disabled={loading || !selectedSlot}
        className={`w-full py-2 px-4 rounded-md text-white font-medium ${
          loading || !selectedSlot
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-emerald-600 hover:bg-emerald-700'
        }`}
      >
        {loading ? 'Scheduling...' : 'Schedule Appointment'}
      </button>

      {toastMessage && <Toast message={toastMessage} onClose={hideToast} />}
    </div>
  );
} 